import React from 'react';

const STORE_NAME_FALLBACK = '—';

export default function WarehouseProductCard({
    detail, binDraft, setBinDraft, busyBin, busyRS, onSaveBin, onToggleRS,
    overstockDraft, setOverstockDraft, busyOverstock, onSaveOverstock,
    binFlash = false, overstockFlash = false, rfidShelvedBoxes = 0,
}) {
    if (!detail) return null;
    const summary = summarizeVariants(detail.variants || []);
    const rfidNote = rfidShelvedBoxes > 0 ? (
        <div className="warehouse-overstock-rfid-note" role="note">
            {rfidShelvedBoxes} tagged box{rfidShelvedBoxes === 1 ? '' : 'es'} shelved here — the next box move rewrites this value from the boxes. Edit the Overstock page instead.
        </div>
    ) : null;

    return (
        <div style={cardStyle}>
            <div style={identityStyle}>
                <div style={thumbStyle}>
                    {detail.image
                        ? <img src={detail.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
                        : glyphFor(detail.product_type)
                    }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={titleStyle}>{detail.title || STORE_NAME_FALLBACK}</div>
                    <div style={metaStyle}>{detail.vendor || ''} · {summary}</div>
                </div>
            </div>

            <div style={reqRowStyle}>
                <div style={{ flex: 1, lineHeight: 1.25 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#202223' }}>Require scanning to pick</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>No manual check-off for pickers.</div>
                </div>
                <Toggle on={!!detail.require_scanning} disabled={busyRS} onClick={() => onToggleRS?.()} />
            </div>

            {detail.bin_level === 'product' && (
                <div className={binFlash ? 'warehouse-bin-band--save-flash' : undefined} style={binBandStyle}>
                    <div style={{ flex: 1 }}>
                        <div style={binLabelStyle}>Bin location</div>
                        <div style={binHelperStyle}>Cascades to all sizes</div>
                    </div>
                    <input
                        value={binDraft}
                        onChange={(e) => setBinDraft?.(e.target.value)}
                        onBlur={() => onSaveBin?.()}
                        disabled={busyBin}
                        placeholder="—"
                        style={binInputStyle}
                    />
                </div>
            )}
            {detail.overstock_bin_level === 'product' && rfidNote}
            {detail.overstock_bin_level === 'product' && (
                <div className={overstockFlash ? 'warehouse-bin-band--save-flash' : undefined} style={binBandStyle}>
                    <div style={{ flex: 1 }}>
                        <div style={binLabelStyle}>Overstock location</div>
                        <div style={binHelperStyle}>Cascades to all sizes</div>
                    </div>
                    <input
                        value={overstockDraft ?? ''}
                        onChange={(e) => setOverstockDraft?.(e.target.value)}
                        onBlur={() => onSaveOverstock?.()}
                        disabled={busyOverstock}
                        placeholder="—"
                        style={binInputStyle}
                    />
                </div>
            )}
            {detail.bin_level === null && (
                <div style={infoBandStyle}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1a4fa0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
                        <circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/>
                    </svg>
                    <div style={{ fontSize: 11.5, color: '#3a5a86', lineHeight: 1.35 }}>
                        Bins aren't set up for this location. Enable in <b>Settings → Picking</b> first.
                    </div>
                </div>
            )}
        </div>
    );
}

function Toggle({ on, onClick, disabled }) {
    return (
        <button
            type="button"
            onClick={() => onClick?.()}
            disabled={disabled}
            aria-pressed={on}
            style={{
                width: 44, height: 26, borderRadius: 999, border: 'none',
                background: on ? '#1a4fa0' : '#c9cccf',
                position: 'relative', cursor: disabled ? 'wait' : 'pointer',
                padding: 0, transition: 'background 0.18s ease',
            }}
        >
            <span style={{
                position: 'absolute', top: 3, left: on ? 21 : 3,
                width: 20, height: 20, borderRadius: '50%',
                background: '#fff', transition: 'left 0.18s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
        </button>
    );
}

function summarizeVariants(variants) {
    const total = variants.length;
    const stocked = variants.filter(v => v.is_active_at_location).length;
    if (total === 0) return 'no variants';
    return `${total} size${total === 1 ? '' : 's'} · ${stocked} stocked`;
}

function glyphFor() {
    return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7l4-3h8l4 3v3a2 2 0 0 1-2 2v8H6v-8a2 2 0 0 1-2-2V7z"/></svg>;
}

const cardStyle = {
    background: '#fff', borderRadius: 16, overflow: 'hidden',
    boxShadow: '0 6px 18px rgba(16,24,40,0.10), 0 1px 2px rgba(16,24,40,0.05)',
};
const identityStyle = { display: 'flex', gap: 11, padding: '11px 13px', alignItems: 'center' };
const thumbStyle = {
    width: 44, height: 44, flex: 'none', borderRadius: 10,
    background: '#f3f4f6', border: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af',
    overflow: 'hidden',
};
const titleStyle = { fontSize: 15, fontWeight: 700, color: '#202223', lineHeight: 1.2 };
const metaStyle = { fontSize: 12, color: '#6d7175', marginTop: 1 };
const reqRowStyle = {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '9px 13px', borderTop: '1px solid #f0f1f3',
};
const binBandStyle = {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '8px 13px', borderTop: '1px solid #f0f1f3',
    background: 'var(--wh-bin-band-bg)',
};
const binLabelStyle = {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
    color: 'var(--wh-bin-label)',
};
const binHelperStyle = { fontSize: 10.5, color: 'var(--wh-bin-helper)' };
const binInputStyle = {
    width: 106, border: '1px solid var(--wh-bin-border)',
    background: '#fff', borderRadius: 9, padding: '6px 10px',
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
    fontSize: 18, letterSpacing: '0.02em', color: 'var(--wh-bin-value)',
    outline: 'none', textTransform: 'uppercase', textAlign: 'center',
};
const infoBandStyle = {
    display: 'flex', gap: 9, padding: '9px 13px',
    borderTop: '1px solid #f0f1f3', background: '#eef4fb', alignItems: 'center',
};
